import React, { useMemo } from "react";

import { PROJECT_PHASE } from "@src/constants";
import PhaseRenderer from "@src/features/staffing_requests/custom_renderer/phase_renderer/phase_renderer";
import SelectionFilter from "@src/features/table_filtering/components/selection_filter/selection_filter";
import { mapToTreeData } from "@src/features/table_filtering/utils/filter_utils";
import { setResourcePlanOverviewTableFilter } from "@src/services/resourcePlanOverviewSlice";
import { RootState, useAppDispatch, useAppSelector } from "@src/setupStore";
import { ResourcePlanOverviewTableRowData } from "@src/types/resource_plan_types";
import { TreeNode } from "@src/types/antd_types";

interface FilterResourcePlanProps {
  disabled: boolean;
  resourcePlans: ResourcePlanOverviewTableRowData[];
}

interface ResourcePlanOverviewFilter {
  title: string;
  treeData: TreeNode[];
  key: string;
  customRenderer?: (node: TreeNode) => React.ReactNode;
}

const FilterResourcePlanOverview: React.FC<FilterResourcePlanProps> = ({ disabled, resourcePlans }) => {
  const dispatch = useAppDispatch();
  const projects = useMemo(() => {
    return resourcePlans.map((resourcePlan: ResourcePlanOverviewTableRowData) => resourcePlan.projects).flat();
  }, [resourcePlans]);

  const { customTableFilters } = useAppSelector((state: RootState) => state.resourcePlanOverviewSlice);

  const filters: ResourcePlanOverviewFilter[] = [
    {
      title: "Projects",
      treeData: mapToTreeData(projects, "name", true),
      key: "project",
    },
    {
      title: "Phase",
      treeData: mapToTreeData(Object.values(PROJECT_PHASE)),
      key: "phase",
      customRenderer: (node: TreeNode) => <PhaseRenderer status={node.title.toString()} />,
    },
    {
      title: "Region",
      treeData: mapToTreeData(projects, "region", true),
      key: "region",
    },
    {
      title: "Country",
      treeData: mapToTreeData(projects, "country", true),
      key: "country",
    },
    {
      title: "Project Manager",
      treeData: mapToTreeData(projects, "projectManager", true),
      key: "projectManager",
    },
  ];

  function onFilterChange(values: string[], key: string): void {
    dispatch(
      setResourcePlanOverviewTableFilter({
        ...customTableFilters,
        [key]: values,
      })
    );
  }

  return (
    <>
      {filters?.map((filter: ResourcePlanOverviewFilter) => (
        <>
          <SelectionFilter
            title={filter.title}
            isDataStatic={false}
            treeData={filter.treeData}
            disabled={disabled}
            customRenderer={filter?.customRenderer}
            defaultKeys={(customTableFilters[filter.key] as string[]) || []}
            onFilter={(values: string[]) => onFilterChange(values, filter.key)}
          />
        </>
      ))}
    </>
  );
};

export default FilterResourcePlanOverview;
